/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink } from "lucide-react";

interface EulaScreenProps {
  onAccept: () => Promise<void>;
  onDecline: () => void;
}

export default function EulaScreen({ onAccept, onDecline }: EulaScreenProps) {
  const [accepting, setAccepting] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      setScrollProgress(100);
      setScrolledToBottom(true);
      return;
    }
    const pct = Math.min(100, (scrollTop / maxScroll) * 100);
    setScrollProgress(pct);
    if (pct >= 95) setScrolledToBottom(true);
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept();
      // Notification permission is now requested earlier and exactly once, by
      // the phone-first onboarding primer (NotificationPrimerScreen). The
      // former Build-89 request here was redundant — removed so there is a
      // single, well-timed ask.
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-10 pb-6 text-center border-b border-border">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center gap-3"
        >
          <div className="w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center shadow-lg">
            <MapPin className="text-primary-foreground" size={28} />
          </div>
          <div>
            <h1 className="text-xl font-bold font-display gradient-text">MyAmbits</h1>
            <h2 className="text-base font-semibold mt-1">Terms of Service &amp; Privacy Policy</h2>
            <p className="text-xs text-muted-foreground mt-1">Please read and accept to continue</p>
          </div>
        </motion.div>
      </div>

      {/* Scroll progress indicator */}
      <div className="h-0.5 bg-muted flex-shrink-0">
        <div
          className="h-full bg-primary transition-all duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Scrollable content */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 px-5 py-5 space-y-5 text-sm text-muted-foreground"
        style={{
          overflowY: 'scroll',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="space-y-5"
        >
          {/* 1. Acceptance of Terms */}
          <section>
            <h3 className="text-base font-semibold text-purple-400 mb-2">1. Acceptance of Terms</h3>
            <p>
              By tapping "I Agree," you agree to be bound by these Terms of Service and our Privacy
              Policy. If you do not agree, tap "Decline" to exit the app. We reserve the right to
              modify these Terms at any time. Continued use of the Service constitutes acceptance of
              any changes.
            </p>
          </section>

          {/* 2. Emergency Services Disclaimer */}
          <section className="glass-card p-4 border border-destructive/30">
            <h3 className="text-base font-semibold text-purple-400 mb-2">
              2. Emergency Services Disclaimer
            </h3>
            <p className="leading-relaxed">
              MyAmbits uses location services to assist users in safety situations.{" "}
              <strong className="text-foreground">
                THIS APP IS NOT A REPLACEMENT FOR OFFICIAL EMERGENCY SERVICES.
              </strong>{" "}
              Always call 911 or your local emergency number in life-threatening situations.
              MyAmbits cannot guarantee that emergency responders will receive or be able to
              identify your location. Location accuracy depends on your device GPS and network
              conditions. By using this app you acknowledge these limitations.
            </p>
          </section>

          {/* 3. Location Data Usage */}
          <section>
            <h3 className="text-base font-semibold text-purple-400 mb-2">3. Location Data Usage</h3>
            <p>
              The Service uses your device location to provide nearby place recommendations, safety
              scores, Walk Me Home tracking, and social features. Location data is shared with your
              designated emergency contacts during active Walk Me Home sessions and with friends
              when location sharing is enabled. You may disable location sharing in Settings at any
              time, though certain features will not function without it.
            </p>
          </section>

          {/* 4. AI Services */}
          <section>
            <h3 className="text-base font-semibold text-purple-400 mb-2">
              4. AI Services (Anthropic Claude)
            </h3>
            <p>
              This app uses Anthropic's Claude AI to provide safety recommendations. Your location
              and queries may be sent to Anthropic, Inc. This data is not used for advertising
              purposes. AI responses are informational only and do not constitute professional
              advice. Session limits apply (20 messages per session).
            </p>
          </section>

          {/* 5. Account and Data */}
          <section>
            <h3 className="text-base font-semibold text-purple-400 mb-2">5. Account and Data</h3>
            <p>
              You must provide accurate information when creating an account and must be at least 13
              years old to use the Service. You are responsible for all activity under your account.
              You may delete your account at any time through the app settings. Upon deletion, your
              personal data will be removed per our Privacy Policy. We reserve the right to
              terminate accounts that violate these Terms.
            </p>
          </section>

          {/* 6. Privacy Policy */}
          <section>
            <h3 className="text-base font-semibold text-purple-400 mb-2">6. Privacy Policy</h3>
            <p>
              Our full Privacy Policy, which describes how we collect, use, and protect your data,
              is available at:
            </p>
            <a
              href="https://myambits.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline mt-1"
            >
              myambits.com/privacy
              <ExternalLink size={12} />
            </a>
          </section>

          {/* Padding so last section isn't hidden behind buttons */}
          <div className="h-4" />
        </motion.div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="flex-shrink-0 px-5 py-4 border-t border-border bg-background space-y-2">
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleAccept}
          disabled={accepting || !scrolledToBottom}
          className="w-full gradient-bg text-primary-foreground py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {accepting ? (
            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
          ) : !scrolledToBottom ? (
            "Read to continue"
          ) : (
            "I Agree"
          )}
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileTap={{ scale: 0.98 }}
          onClick={onDecline}
          className="w-full glass-card py-3.5 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          Decline
        </motion.button>

        <p className="text-[11px] text-muted-foreground text-center pt-1">
          By tapping "I Agree" you confirm you have read and accept these terms.
        </p>
      </div>
    </div>
  );
}
